import type { ScoreEntry } from "$data/types";

const HANDLES_POOL = [
  "lunar.fox",
  "neon.bloom",
  "cyan.echo",
  "frost.kite",
  "ember.ash",
  "glass.tide",
  "midnight.rose",
  "ghost.bolt",
  "quartz.iris",
  "shadow.fawn",
  "velvet.moth",
  "silk.crow",
  "mirror.flux",
  "static.bloom",
  "haze.wolf",
  "spark.wave",
  "amber.storm",
  "pixel.rain",
  "orbit.pulse",
  "echo.drift",
  "sonic.iris",
  "plush.comet",
  "retro.halo",
  "glitch.ash",
  "clover.dusk",
  "maya.71",
  "jules.07",
  "remi.wav",
  "kai.zero",
  "noor.fm",
];

const CATS = [
  "SHAPES",
  "NUMBERS",
  "COLORS",
  "MONTHS",
  "DAYS",
  "FLAVORS",
  "SEASONS",
  "CITIES",
  "MUSIC",
];

/** Shape of a row returned by `GET /api/scoreboard`. */
interface ApiRow {
  id: number;
  handleA: string;
  handleB: string;
  pct: number;
  category: string;
  pulseId: string;
  insertedAt: number;
}

const POLL_MS = 4500;
const FRESH_MS = 4500;

function pickPair(): [string, string] {
  const h1 = HANDLES_POOL[Math.floor(Math.random() * HANDLES_POOL.length)]!;
  let h2: string;
  do {
    h2 = HANDLES_POOL[Math.floor(Math.random() * HANDLES_POOL.length)]!;
  } while (h2 === h1);
  return [h1, h2];
}

function makeSimEntry(opts: {
  id: string;
  minPct?: number;
  maxPct?: number;
  minsAgo?: number;
}): ScoreEntry {
  const min = opts.minPct ?? 55;
  const max = opts.maxPct ?? 99;
  const pct = min + Math.floor(Math.random() * (max - min + 1));
  return {
    id: opts.id,
    pair: pickPair(),
    pct,
    cat: CATS[Math.floor(Math.random() * CATS.length)]!,
    minsAgo: opts.minsAgo ?? Math.floor(Math.random() * 1380),
  };
}

class ScoreboardState {
  entries = $state<ScoreEntry[]>([]);
  fresh = $state<Set<string>>(new Set());
  /** True once a /api/scoreboard fetch has succeeded at least once. */
  isLive = $state<boolean>(false);
  /** True if the current entries come from the simulator fallback. */
  isFallback = $state<boolean>(true);

  private simSeq = 0;
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  /** Set of API ids previously seen — anything new is marked fresh. */
  private knownApiIds = new Set<string>();

  /** Top 3 by pct in the last 24h (distinct pairs). */
  top3 = $derived.by(() => {
    const today = this.entries.filter((e) => e.minsAgo < 1440);
    const seen = new Set<string>();
    return [...today]
      .sort((a, b) => b.pct - a.pct)
      .filter((e) => {
        const k = [...e.pair].sort().join("+");
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      })
      .slice(0, 3);
  });

  /** Feed = entries excluding podium, capped to 40. */
  feed = $derived.by(() => {
    const top3Ids = new Set(this.top3.map((e) => e.id));
    return this.entries.filter((e) => !top3Ids.has(e.id)).slice(0, 40);
  });

  private nextSimId(): string {
    return `sim-${++this.simSeq}`;
  }

  private fromApiRow(row: ApiRow, nowSec: number): ScoreEntry {
    return {
      id: `db-${row.id}`,
      pair: [row.handleA, row.handleB],
      pct: row.pct,
      cat: (row.category || "").toUpperCase(),
      minsAgo: Math.max(0, Math.floor((nowSec - row.insertedAt) / 60)),
    };
  }

  private markFresh(id: string) {
    const f = new Set(this.fresh);
    f.add(id);
    this.fresh = f;
    setTimeout(() => {
      const cur = new Set(this.fresh);
      cur.delete(id);
      this.fresh = cur;
    }, FRESH_MS);
  }

  /** Seed the feed with simulated entries — used only when DB is empty/unreachable. */
  private seedSim() {
    const items: ScoreEntry[] = [];
    items.push(
      makeSimEntry({
        id: this.nextSimId(),
        minPct: 95,
        maxPct: 99,
        minsAgo: 18,
      }),
    );
    items.push(
      makeSimEntry({
        id: this.nextSimId(),
        minPct: 92,
        maxPct: 96,
        minsAgo: 47,
      }),
    );
    items.push(
      makeSimEntry({
        id: this.nextSimId(),
        minPct: 90,
        maxPct: 94,
        minsAgo: 102,
      }),
    );
    for (let i = 0; i < 22; i++) {
      items.push(
        makeSimEntry({
          id: this.nextSimId(),
          minPct: 58,
          maxPct: 92,
          minsAgo: 5 + i * 60 + Math.floor(Math.random() * 40),
        }),
      );
    }
    items.sort((a, b) => a.minsAgo - b.minsAgo);
    this.entries = items;
  }

  /** Push one fresh simulated entry — keeps the feed alive when DB is empty. */
  private pushSim() {
    const entry = makeSimEntry({ id: this.nextSimId(), minsAgo: 0 });
    const aged = this.entries.map((e, i) =>
      i === 0 ? e : { ...e, minsAgo: e.minsAgo + 1 },
    );
    this.entries = [entry, ...aged].slice(0, 50);
    this.markFresh(entry.id);
  }

  /** One poll cycle. Returns whether we got real DB rows. */
  private async pollOnce(): Promise<{ ok: boolean; hasRows: boolean }> {
    let payload: { top3: ApiRow[]; feed: ApiRow[] } | null = null;
    try {
      const res = await fetch("/api/scoreboard?limit=40", {
        headers: { accept: "application/json" },
        cache: "no-store",
      });
      if (!res.ok) return { ok: false, hasRows: false };
      payload = (await res.json()) as { top3: ApiRow[]; feed: ApiRow[] };
    } catch {
      return { ok: false, hasRows: false };
    }

    this.isLive = true;

    const nowSec = Math.floor(Date.now() / 1000);
    const seen = new Set<string>();
    const merged: ScoreEntry[] = [];
    for (const row of [...(payload.top3 ?? []), ...(payload.feed ?? [])]) {
      const id = `db-${row.id}`;
      if (seen.has(id)) continue;
      seen.add(id);
      merged.push(this.fromApiRow(row, nowSec));
    }

    if (merged.length === 0) {
      return { ok: true, hasRows: false };
    }

    for (const e of merged) {
      if (!this.knownApiIds.has(e.id)) {
        this.knownApiIds.add(e.id);
        this.markFresh(e.id);
      }
    }

    this.entries = merged;
    this.isFallback = false;
    return { ok: true, hasRows: true };
  }

  /** Begin polling. Real DB rows take precedence over simulated demo data. */
  async start() {
    this.stop();
    const first = await this.pollOnce();
    if (!first.hasRows && this.entries.length === 0) {
      this.seedSim();
      this.isFallback = true;
    }

    const tick = async () => {
      const { ok, hasRows } = await this.pollOnce();
      if (!hasRows) {
        this.isFallback = true;
        if (!ok || this.entries.every((e) => e.id.startsWith("sim-"))) {
          this.pushSim();
        }
      }
      this.pollTimer = setTimeout(tick, POLL_MS);
    };
    this.pollTimer = setTimeout(tick, POLL_MS);
  }

  stop() {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  }

  /** POST a freshly-played pulse to the scoreboard. Best-effort, never throws. */
  async publishResult(input: {
    handleA: string;
    handleB: string;
    pct: number;
    category: string;
    pulseId: string;
  }): Promise<boolean> {
    try {
      const res = await fetch("/api/scoreboard", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      });
      return res.ok;
    } catch {
      return false;
    }
  }
}

export const scoreboard = new ScoreboardState();

export function fmtTime(minsAgo: number): string {
  if (minsAgo < 1) return "just now";
  if (minsAgo < 60) return `${minsAgo}m ago`;
  if (minsAgo < 1440) return `${Math.floor(minsAgo / 60)}h ago`;
  return `${Math.floor(minsAgo / 1440)}d ago`;
}
